import Cookies from 'js-cookie';
import styles from "./Register.module.css";
import Filler from "../../assets/Filler.jpeg";
import Logo from "../../assets/LogoPrincipal.png";
import {Link, useNavigate} from "react-router-dom";
import {Field, FieldProps, Form, Formik} from 'formik';
import * as Yup from 'yup';
import InputMask from "react-input-mask";
import CustomErrorMessage from "../../components/CustomErrorMessage/CustomErrorMessage";
import {useTranslation} from "react-i18next";
import api from "../../utils/axios/api";
import { useState } from 'react';

function digitsOnly(value: string): string {
    return value.replace(/\D/g, '');
}

function isValidCpf(value: string): boolean {
    const cpf = digitsOnly(value);
    if (cpf.length !== 11) return false;
    if (/^(\d)\1{10}$/.test(cpf)) return false;

    let sum = 0;
    for (let i = 0; i < 9; i++) sum += Number(cpf[i]) * (10 - i);
    let d1 = (sum * 10) % 11;
    if (d1 === 10) d1 = 0;
    if (d1 !== Number(cpf[9])) return false;

    sum = 0;
    for (let i = 0; i < 10; i++) sum += Number(cpf[i]) * (11 - i);
    let d2 = (sum * 10) % 11;
    if (d2 === 10) d2 = 0;
    return d2 === Number(cpf[10]);
}

type SignUpData = {
    name: string,
    email: string,
    password: string,
    phone: string,
    cpf: string,
    cep: string,
    adress: string,
    adressSurname: string,
    estado: string,
    city: string
    district: string
    number: string,
    complement: string,
    occupation: string,
}

function Register() {
    const {t} = useTranslation("", {keyPrefix: "cadastro"});
    const [noNumber, setNoNumber] = useState(false);

    const navigate = useNavigate()

    const email = Cookies.get('email') ?? ''
    const name = Cookies.get('name') ?? ''
    const picture = Cookies.get('picture') ?? undefined

    const SignupSchema = Yup.object().shape({
        name: Yup.string()
            .required(t('required')),
        email: Yup
            .string()
            .email(t('invalidEmail'))
            .required(t('required')),
        password: Yup
            .string()
            .required(t('required'))
            .min(8, 'A senha deve ter pelo menos 8 caracteres'),
        phone: Yup
            .string()
            .required(t('required'))
            .matches(/^(?:(?:\+|00)?(55)\s?)?(?:\((?:[14689][1-9]|2[12478]|3[1-8]|5[13-5]|7[134579])\)\s?)?(?:[2-8]|9[1-9])[0-9]{3}-?[0-9]{4}$/, 'Telefone inválido'),
        cpf: Yup
            .string()
            .required(t('required'))
            .test('cpf-valid', t('invalidCpf'), (value) => isValidCpf(value ?? '')),
        cep: Yup
            .string()
            .required(t('required'))
            .matches(/^\d{5}-\d{3}$/, t('requireCEP')),
        adress: Yup
            .string()
            .required(t('required')),
        adressSurname: Yup
            .string(),
        estado: Yup
            .string()
            .required(t('required')),
        district: Yup
            .string()
            .required(t('required')),
        city: Yup
            .string()
            .required(t('required')),
        number: Yup
            .string(),
        complement: Yup
            .string(),
        occupation: Yup
            .string()
            .required(t('required')),
    });

    async function buscaCEP(cep: string, setFieldValue: (field: string, value: string) => void) {
        if (cep.length === 9) {
            try {
                const response = await fetch(`https://viacep.com.br/ws/${cep.replace('-', '')}/json/`);
                const data = await response.json();
                if (!data.erro) {
                    setFieldValue('adress', data.logradouro);
                    setFieldValue('city', data.localidade);
                    setFieldValue('district', data.bairro)
                    setFieldValue('estado', data.uf);
                }
            } catch (error) {
                console.error('Erro ao buscar CEP:', error);
            }
        }
    }

    async function handleRegister(values: SignUpData) {
        try {
            await api.post('/user', {
                name: values.name,
                password: values.password,
                email: values.email,
                isUserDeleted: false,
                confirmedEmail: false,
                document: digitsOnly(values.cpf),
                documentType: 'CPF',
                role: 'Client',
                image: picture,
                telephone: values.phone,
                address: {
                    state: values.estado,
                    city: values.city,
                    street: values.adress,
                    district: values.district,
                    zipCode: values.cep.replace('-', ''),
                    complement: values.complement,
                    number: values.number || null,
                    name: values.adressSurname
                },
                occupation: values.occupation,
            })

            console.log(values.occupation)

            Cookies.remove('email')
            Cookies.remove('name')
            Cookies.remove('picture')

            navigate('/cadastrofinalizado')
        } catch {
            alert('Erro ao cadastrar usuário.')
        }
    }

    return (
        <div className={styles.body}>

            <div className={styles.containerRegister}>

                <img src={Logo} className={styles.Logo}/>

                <div className={styles.wrapperTop}>
                    <p>{t('haveAccount')}</p>
                    <Link className={styles.textLink} to={"/login"}>{t('Login')}</Link>
                </div>

                <h1>Cadastrar</h1>

                <Formik
                    initialValues={{
                        name,
                        email,
                        password: '',
                        phone: '',
                        cpf: '',
                        cep: '',
                        adress: '',
                        adressSurname: '',
                        estado: '',
                        district: '',
                        city: '',
                        number: '',
                        complement: '',
                        occupation: '',
                    }}
                    validationSchema={SignupSchema}
                    validateOnChange
                    validateOnBlur
                    onSubmit={values => handleRegister(values)}
                >
                    {({errors, touched, handleSubmit, setFieldValue, isValid, isSubmitting}) => (
                        <Form onSubmit={handleSubmit} className={styles.containerForm}>

                            <div className={styles.wrapperInput}>
                                <label htmlFor="name">{t('name')} <span className={styles.asterisco}>*</span></label>
                                <Field className={styles.input} name="name" placeholder="Ex: João Silva"/>
                                {touched.name && errors.name && <CustomErrorMessage name="name"/>}
                            </div>

                            <div className={styles.wrapperInput}>
                                <label htmlFor="email">{t('email')} <span className={styles.asterisco}>*</span></label>
                                <Field className={styles.input} name="email" type="email" placeholder="Ex: joao@email.com"/>
                                {touched.email && errors.email && <CustomErrorMessage name="email"/>}
                            </div>

                            <div className={styles.wrapperInput}>
                                <label htmlFor="password">{t('password')} <span className={styles.asterisco}>*</span></label>
                                <Field className={styles.input} name="password" type="password" placeholder="Mínimo 8 caracteres"/>
                                {touched.password && errors.password && <CustomErrorMessage name="password"/>}
                            </div>

                            <div className={styles.wrapperInput}>
                                <label htmlFor="phone">{t('phone')} <span className={styles.asterisco}>*</span></label>
                                <Field name="phone">
                                    {({field}: FieldProps) => (
                                        <InputMask
                                            {...field}
                                            mask="(99) 99999-9999"
                                            className={styles.input}
                                            onChange={(e) => setFieldValue('phone', e.target.value)}
                                            placeholder="(11) 99999-9999"
                                        />
                                    )}
                                </Field>
                                {touched.phone && errors.phone && <CustomErrorMessage name="phone"/>}
                            </div>

                            <div className={styles.wrapperInput}>
                                <label htmlFor="cpf">CPF <span className={styles.asterisco}>*</span></label>
                                <Field name="cpf">
                                    {({field}: FieldProps) => (
                                        <InputMask
                                            {...field}
                                            mask="999.999.999-99"
                                            className={styles.input}
                                            onChange={(e) => setFieldValue('cpf', e.target.value)}
                                            placeholder="000.000.000-00"
                                        />
                                    )}
                                </Field>
                                {touched.cpf && errors.cpf && <CustomErrorMessage name="cpf"/>}
                            </div>

                            <div className={styles.wrapperInput2}>
                                <div className={styles.halfInput}>
                                    <label htmlFor="cep">CEP <span className={styles.asterisco}>*</span></label>
                                    <Field name="cep">
                                        {({field}: FieldProps) => (
                                            <InputMask
                                                {...field}
                                                mask="99999-999"
                                                className={styles.input}
                                                onChange={(e) => setFieldValue('cep', e.target.value)}
                                                placeholder="00000-000"
                                                onBlur={() => buscaCEP(field.value, setFieldValue)}
                                            />
                                        )}
                                    </Field>
                                    {touched.cep && errors.cep && <CustomErrorMessage name="cep"/>}
                                </div>
                                <div className={styles.halfInput}>
                                    <label htmlFor="estado">{t('estado')} <span className={styles.asterisco}>*</span></label>
                                    <Field className={styles.input} name="estado" type="text" placeholder="Ex: SP"/>
                                    {touched.estado && errors.estado && <CustomErrorMessage name="estado"/>}
                                </div>
                            </div>

                            <div className={styles.wrapperInput2}>
                                <div className={styles.halfInput}>
                                    <label htmlFor="city">{t('city')} <span className={styles.asterisco}>*</span></label>
                                    <Field className={styles.input} name="city" type="text" placeholder="Ex: São Paulo"/>
                                    {touched.city && errors.city && <CustomErrorMessage name="city"/>}
                                </div>
                                <div className={styles.halfInput}>
                                    <label htmlFor="district">{t('district')} <span className={styles.asterisco}>*</span></label>
                                    <Field className={styles.input} name="district" type="text" placeholder="Ex: Centro"/>
                                    {touched.district && errors.district && <CustomErrorMessage name="district"/>}
                                </div>
                            </div>

                            <div className={styles.wrapperInput}>
                                <label htmlFor="adressSurname">{t('adressSurname')}</label>
                                <Field className={styles.input} name="adressSurname" type="text" placeholder="Ex: Casa, Trabalho"/>
                                {touched.adressSurname && errors.adressSurname &&
                                    <CustomErrorMessage name="adressSurname"/>}
                            </div>

                            <div className={styles.wrapperInput}>
                                <label htmlFor="adress">{t('adress')} <span className={styles.asterisco}>*</span></label>
                                <Field className={styles.input} name="adress" type="text" placeholder="Ex: Rua das Flores"/>
                                {touched.adress && errors.adress && <CustomErrorMessage name="adress"/>}
                            </div>

                            <div className={styles.wrapperInput2}>
                                <div className={styles.halfInput}>
                                    <label htmlFor="number">{t('number')} <span className={styles.asterisco}>*</span></label>
                                    <Field
                                        className={styles.input}
                                        name="number"
                                        type="text"
                                        placeholder="Ex: 123"
                                        disabled={noNumber}
                                    />
                                    <label className={styles.noNumberToggle}>
                                        <input
                                            type="checkbox"
                                            checked={noNumber}
                                            onChange={(e) => {
                                                setNoNumber(e.target.checked);
                                                setFieldValue('number', e.target.checked ? '0' : '');
                                            }}
                                        />
                                        Sem número
                                    </label>
                                    {touched.number && errors.number && <CustomErrorMessage name="number"/>}
                                </div>
                                <div className={styles.halfInput}>
                                    <label htmlFor="complement">{t('complement')}</label>
                                    <Field className={styles.input} name="complement" type="text" placeholder="Ex: Apto 42"/>
                                    {touched.complement && errors.complement && <CustomErrorMessage name="complement"/>}
                                </div>
                            </div>

                            <div className={styles.wrapperInput}>
                                <label htmlFor="occupation">{t('occupation')} <span className={styles.asterisco}>*</span></label>
                                <Field className={styles.input} name="occupation" placeholder="Ex: Pedreiro, Engenheiro"/>
                                {touched.name && errors.name && <CustomErrorMessage name="occupation"/>}
                            </div>

                            <button type="submit" disabled={!isValid || isSubmitting} className={styles.button}>
                                {isSubmitting ? t('registerLoading') : t('register')}
                            </button>

                        </Form>
                    )}
                </Formik>


            </div>

            <img src={Filler} className={styles.filler}/>
        </div>
    )
}

export default Register;
